import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { FormComboSelectChange } from 'app/components/form/FormComboSelect'
import { MODEL_FIELDS } from 'app/generated/modelFields'
import { logError } from 'lib/logging'

// Accepts either a native input ChangeEvent (FormInput) or the synthetic
// FormComboSelectChange emitted by the migrated FormSelect combo (TRK-5214-2).
// Both expose `target.{name,value}`, which is all handleChange reads.
type OpsSearchChange = React.ChangeEvent<HTMLInputElement | HTMLSelectElement> | FormComboSelectChange

export type UseOpsSearchToolReturn = {
  loaderData: any
  formData: { query: string; index: string }
  handleChange: (event: OpsSearchChange) => void
  submitHandler: (event: any) => void
}

// Maps an Ops Search Tool index name to its GraphQL list query and by-id query.
// The by-id query name doubles as the MODEL_FIELDS key (e.g. `callDatum`, `location`),
// so the same key selects the right field set for either query.
const INDEX_QUERIES: Record<string, { list: string; byId: string }> = {
  activity: { list: `activities`, byId: `activity` },
  appointment: { list: `appointments`, byId: `appointment` },
  call: { list: `calls`, byId: `call` },
  call_data: { list: `callData`, byId: `callDatum` },
  contact: { list: `contacts`, byId: `contact` },
  location: { list: `locations`, byId: `location` },
  patient: { list: `patients`, byId: `patient` },
  transaction: { list: `transactions`, byId: `transaction` },
  user: { list: `users`, byId: `user` },
  workspace: { list: `workspaces`, byId: `workspace` },
}

// Raw GraphQL request that bypasses the urql normalized cache. The ops tool is a debug
// tool that must show exactly what the API returns; urql's graphcache (which is
// schema-unaware here) silently nullifies a whole entity when the server omits a field
// the query requested (e.g. stale fields in MODEL_FIELDS), which would hide real results.
async function rawGraphql<T>(query: string, variables: Record<string, unknown>): Promise<T> {
  const res = await fetch(`/graphql`, {
    method: `POST`,
    headers: { 'Content-Type': `application/json` },
    credentials: `include`,
    body: JSON.stringify({ query, variables }),
  })
  const json = (await res.json()) as { data?: T; errors?: { message: string }[] }
  if (json.errors?.length) throw new Error(json.errors.map((e) => e.message).join(`; `))
  if (!json.data) throw new Error(`No data returned`)
  return json.data
}

export function useOpsSearchTool(): UseOpsSearchToolReturn {
  const [searchParams, setSearchParams] = useSearchParams()
  const urlQuery = searchParams.get(`query`) ?? ``
  const urlIndex = searchParams.get(`index`) ?? ``

  const [formData, setFormData] = useState({ query: urlQuery, index: urlIndex })
  const [loaderData, setLoaderData] = useState<any>(null)

  // Keep the form inputs in sync when the URL changes (e.g. direct navigation, back/forward).
  useEffect(() => {
    setFormData({ query: urlQuery, index: urlIndex })
  }, [urlQuery, urlIndex])

  // Run the search whenever the URL query/index changes.
  useEffect(() => {
    if (!urlQuery && !urlIndex) {
      setLoaderData(null)
      return
    }

    const queries = INDEX_QUERIES[urlIndex]
    if (!queries) {
      setLoaderData({ error: `Unknown index: ${urlIndex}` })
      return
    }

    let cancelled = false

    const run = async () => {
      try {
        const trimmed = urlQuery.trim()

        // Always attempt an exact by-id lookup first. Model ids are not uniform: some
        // are 24-char ObjectIds (contact, location, user, workspace) while others are
        // composite PMS/provider strings (e.g. transaction `C-10417-1225395-...`,
        // call `CAL0...`). Gating by id shape would skip the by-id path for those
        // models, so we try it unconditionally. The query just resolves to null when
        // the id does not match a record, in which case we fall back to free-text search.
        //
        // The by-id lookup is best-effort: it is wrapped in its own try/catch so a
        // failure never aborts the search flow. The `call(id:)` resolver (and others)
        // throws INTERNAL_SERVER_ERROR — not null — when the record belongs to a
        // different org than the ops user's current session (TRK-5250). Letting that
        // error propagate to the outer catch would surface it as a user-facing error
        // and skip the free-text fallback, breaking cross-org lookups. Swallow it here
        // (a miss, real or errored, just means "try free-text next").
        //
        // `cancelled` only guards the final state write (so a stale run from a previous
        // query/index does not clobber the current result). It must NOT abort the search
        // flow between the two awaits: under React StrictMode the first effect pass is
        // cancelled immediately, and bailing mid-flow there would skip the fallback list
        // search and leave the UI empty.
        if (trimmed) {
          try {
            const byIdOp = queries.byId.charAt(0).toUpperCase() + queries.byId.slice(1)
            const byIdFields = MODEL_FIELDS[queries.byId] ?? `id`
            const byIdQuery = `query ${byIdOp}ById($id: ID!) { ${queries.byId}(id: $id) { ${byIdFields} } }`
            const byIdData = await rawGraphql<Record<string, unknown>>(byIdQuery, { id: trimmed })
            const record = byIdData[queries.byId]
            if (record) {
              if (!cancelled) setLoaderData({ hits: [record], total: 1, page: 1 })
              return
            }
          } catch (byIdError) {
            // By-id failed (e.g. cross-org INTERNAL_SERVER_ERROR); fall through to
            // free-text list search below. Not user-facing — the list search is the
            // authoritative result path for a miss.
            logError(`opsSearch by-id lookup failed; falling back to list search`, byIdError)
          }
        }

        // No exact id match (or empty query): run a free-text list search.
        const opName = queries.list.charAt(0).toUpperCase() + queries.list.slice(1)
        const fields = MODEL_FIELDS[queries.list] ?? `id`
        const query = `query ${opName}List($search: String) { ${queries.list}(search: $search) { hits { ${fields} } total page } }`
        const data = await rawGraphql<Record<string, { hits: unknown[]; total: number; page: number }>>(query, {
          search: trimmed || undefined,
        })
        if (!cancelled) setLoaderData(data[queries.list])
      } catch (e: any) {
        logError(`opsSearch error`, e)
        if (cancelled) return
        setLoaderData({ error: e?.message ?? String(e) })
      }
    }

    run()

    return () => {
      cancelled = true
    }
  }, [urlQuery, urlIndex])

  const handleChange = (event: OpsSearchChange) => {
    const { name, value } = event.target
    setFormData((prevFormData) => ({ ...prevFormData, [name]: value }))
  }

  const submitHandler = useCallback(
    (event: any) => {
      event?.preventDefault?.()
      setSearchParams({ query: formData.query, index: formData.index })
    },
    [formData.query, formData.index, setSearchParams],
  )

  return { loaderData, formData, handleChange, submitHandler }
}
